import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Typography, Tooltip } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import InboxOutlinedIcon from '@material-ui/icons/InboxOutlined';
import SearchIcon from '@material-ui/icons/Search';
import KanbanCard from './KanbanCard';
import { getTokens, brandAlpha } from '../../styles/tokens';
import { i18n } from '../../translate/i18n';

// TODO i18n: chaves `kanban.*` novas com defaultValue (i18next aceita nativamente)
const t = (key, fallback) => i18n.t(key, { defaultValue: fallback });

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const useStyles = makeStyles(theme => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    shell: {
      display: 'flex',
      flexDirection: 'column',
      flex: 'none',
      width: 288,
      maxHeight: 'calc(100vh - 236px)',
      minHeight: 180,
      [theme.breakpoints.down('xs')]: {
        width: 'calc(100vw - 56px)',
        scrollSnapAlign: 'start',
        maxHeight: 'calc(100vh - 280px)',
      },
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      backgroundColor: tk.muted,
      border: `1px solid ${tk.line}`,
      borderRadius: tk.radius.md,
      boxShadow: tk.shadow.card,
      fontFamily: tk.font,
      transition: 'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
      '&:hover $handle': { opacity: 1 },
      '@media (hover: none)': {
        '& $handle': { opacity: 1 },
      },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
    columnDragging: {
      boxShadow: tk.shadow.soft,
      borderColor: tk.brand,
    },
    columnOver: props => ({
      backgroundColor: brandAlpha(props.color, theme.palette.type === 'dark' ? 0.14 : 0.08),
      borderColor: brandAlpha(props.color, 0.45),
    }),
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '12px 12px 10px',
      minWidth: 0,
    },
    handle: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: -6,
      marginRight: -4,
      color: tk.textMuted,
      cursor: 'grab',
      opacity: 0,
      transition: 'opacity 150ms ease, color 150ms ease',
      '&:hover': { color: tk.textSecondary },
      '&:active': { cursor: 'grabbing' },
      '&:focus-visible': { opacity: 1, outline: 'none', borderRadius: tk.radius.badge, boxShadow: tk.shadow.glow },
    },
    dot: props => ({
      width: 8,
      height: 8,
      borderRadius: '50%',
      flex: 'none',
      backgroundColor: props.color,
    }),
    title: {
      flex: 1,
      minWidth: 0,
      fontFamily: tk.font,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.3,
      color: tk.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // contador neutro: a cor da etapa fica só no ponto
    count: {
      flex: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 24,
      height: 22,
      padding: '0 8px',
      borderRadius: tk.radius.badge,
      fontFamily: tk.font,
      fontSize: 12,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
      color: tk.status.neutral.fg,
      backgroundColor: tk.status.neutral.bg,
    },
    total: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 6,
      padding: '0 12px 10px',
      fontFamily: tk.font,
      fontSize: 12,
      color: tk.textSecondary,
      '& strong': {
        fontWeight: 600,
        color: tk.text,
        fontVariantNumeric: 'tabular-nums',
      },
    },
    list: {
      flex: 1,
      minHeight: 96,
      padding: '2px 8px 8px',
      overflowY: 'auto',
      overflowX: 'hidden',
      ...theme.scrollbarStyles,
    },
    empty: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      margin: '4px 2px 8px',
      padding: '20px 12px',
      borderRadius: tk.radius.sm,
      border: `1px dashed ${tk.line}`,
      color: tk.textMuted,
      textAlign: 'center',
      fontFamily: tk.font,
      fontSize: 12,
      lineHeight: 1.4,
      '& svg': { fontSize: 22, opacity: 0.8 },
      '& strong': { fontWeight: 500, color: tk.textSecondary },
    },
  };
});

const KanbanColumn = ({
  id,
  title,
  tickets,
  totalCount,
  filtered,
  color,
  index,
  updateTicket,
  isAdmin,
}) => {
  const classes = useStyles({ color: color || '#757575' });

  const totalValue = tickets.reduce((acc, ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return acc + (Number.isFinite(opportunityValue) ? opportunityValue : 0);
  }, 0);

  const countLabel =
    filtered && totalCount !== tickets.length
      ? `${tickets.length}/${totalCount}`
      : String(tickets.length);

  return (
    <Draggable draggableId={id} index={index} isDragDisabled={!isAdmin}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={classes.shell}
        >
          <Droppable droppableId={id} type="CARD">
            {(dropProvided, dropSnapshot) => (
              <div
                className={[
                  classes.column,
                  snapshot.isDragging && classes.columnDragging,
                  dropSnapshot.isDraggingOver && classes.columnOver,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className={classes.header}>
                  {isAdmin && (
                    <Tooltip title={t('kanban.reorderLane', 'Arraste para reordenar')}>
                      <div
                        {...provided.dragHandleProps}
                        className={classes.handle}
                        aria-label={t('kanban.reorderLane', 'Arraste para reordenar')}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </div>
                    </Tooltip>
                  )}
                  <span className={classes.dot} aria-hidden="true" />
                  <Tooltip title={title}>
                    <Typography component="h2" className={classes.title}>
                      {title}
                    </Typography>
                  </Tooltip>
                  <span className={classes.count} aria-label={`${tickets.length} tickets`}>
                    {countLabel}
                  </span>
                </div>
                {totalValue > 0 && (
                  <div className={classes.total}>
                    <span>{t('kanban.total', 'Total')}</span>
                    <strong>{currency.format(totalValue)}</strong>
                  </div>
                )}
                <div
                  className={classes.list}
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                >
                  {tickets.map((ticket, ticketIndex) => (
                    <KanbanCard
                      key={ticket.id}
                      ticket={ticket}
                      index={ticketIndex}
                      updateTicket={updateTicket}
                    />
                  ))}
                  {tickets.length === 0 && !dropSnapshot.isDraggingOver && (
                    <div className={classes.empty}>
                      {filtered ? <SearchIcon /> : <InboxOutlinedIcon />}
                      <strong>
                        {filtered
                          ? t('kanban.emptyFiltered', 'Nenhum resultado')
                          : t('kanban.emptyLane', 'Nenhum atendimento')}
                      </strong>
                      <span>
                        {filtered
                          ? t('kanban.emptyFilteredHint', 'Ajuste a busca para ver mais cards.')
                          : t('kanban.emptyLaneHint', 'Arraste um card para esta etapa.')}
                      </span>
                    </div>
                  )}
                  {dropProvided.placeholder}
                </div>
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanColumn;
